<template>
	<div class="content-header has-bottom-divider has-padding-top-3 has-margin">
		<div class="field is-grouped is-flex-grow-1 is-align-items-end">
			<VLink
				v-if="canEditConnection"
				:text="$t('cb.connectionList.addConnection')"
				icon="plus"
				:hasUnderline="true"
				@click.prevent.stop="createNewClicked"
				class="has-margin-right-3 has-margin-bottom-05"
			/>

			<VSearch v-model="search" />
		</div>
	</div>

	<div class="scrollable-content">
		<SortableDataTable
			:scrollable="false"
			:rowClickedFn="rowClicked"
			defaultSort="last_time_edited"
			sortDirection="DESC"
			:data="filteredData"
			:columns="columns"
			:emptyText="$t('cb.connectionList.table.noResults')"
		/>
	</div>
</template>

<script>
import { useStore } from 'vuex';
import { reactive, toRefs } from '@vue/reactivity';
import { getStoreAction, getStoreMutation } from '@assets/scripts/store/config';
import { formatForTable } from '@modules/ConnectionBuilder/components/connection';
import Helpers from '@assets/scripts/helpers';
import { useApiAsync } from '@assets/scripts/composables/useApi';
import { useCreateNewOnKeydown } from '@assets/scripts/composables/useCreateNewOnKeydown';
import usePermission from '@assets/scripts/composables/usePermission';
import { GET_CONNECTIONS } from '@modules/ConnectionBuilder/endpoints';

export default {
	name: 'ConnectionList',
	data: function () {
		return {
			search: '',
		};
	},
	async setup () {
		const store = useStore();

		const state = reactive({
			connections: [],
			/**
			 * Boolean to indicate whether the current
			 * user can create connections
			 */
			canEditConnection: usePermission('Upsert', 'ConnectionBuilder'),
		});

		const createNewClicked = () => {
			store.dispatch(
				getStoreAction('TOGGLE_CREATE_NEW', 'CB'),
				true
			);
		};
		if(state.canEditConnection) {
			useCreateNewOnKeydown(createNewClicked);
		}

		const loadConnections = async () => {
			// get all Connection Documents
			state.connections = await useApiAsync(GET_CONNECTIONS);
		};

		// subscribe to store action
		const unsubscribeAction = store.subscribeAction({
			after: async ({ type }) => {
				// check action type and relevant connection GUID
				if (
					type === getStoreAction('UPDATE_CONNECTION_LIST', 'CB')
				) {
					// reload document list if needed
					await loadConnections(true);
				}
			}
		});

		await loadConnections();

		return { ...toRefs(state), unsubscribeAction, createNewClicked};
	},
	computed: {
		columns: function() {
			return [
				{
					label: this.$t('cb.connectionList.table.columns.doc_name'),
					field: 'name',
					sortable: true,
					searchable: true,
					cellClass: 'has-text-weight-semibold',
					// width: '30%',
				},
				{
					label: this.$t('cb.connectionList.table.columns.type'),
					field: 'type',
					sortable: true,
					searchable: true,
					// width: '15%',
				},
				{
					label: this.$t('cb.connectionList.table.columns.last_time_edited'),
					field: 'last_time_edited',
					sortable: true,
					searchable: true,
					isDate: true,
					// width: '25%',
				},
				{
					label: '',
					field: 'edit',
					sortable: false,
					default: '',
					component: 'VButton',
					cellClass: 'is-button-tool',
					args: {
						edit: {
							title: this.$t('general.edit'),
							isTool: true,
							icon: 'edit',
						},
						view: {
							title: this.$t('general.inspect'),
							isTool: true,
							icon: 'eye',
						}
					},
					click: this.rowClicked,
				},
				{
					label: '',
					field: 'delete',
					sortable: false,
					default: '',
					component: 'VButton',
					cellClass: 'is-button-tool',
					args: {
						href: '',
						title: this.$t('general.delete'),
						isTool: true,
						class: '',
						icon: 'delete',
					},
					click: this.deleteClicked,
				},
			];
		},
		tableFields: function () {
			return formatForTable(this.connections);
		},
		searchableCols: function () {
			return Helpers.getSearchableColumns(this.columns);
		},
		filteredData: function () {
			// loop over all connections
			const newData = this.tableFields;

			// return if no rows match
			if (this.search.length === 0 || newData.length === 0) return newData;

			// filter on search string
			return Helpers.filterByString(
				newData,
				this.search,
				this.searchableCols
			);
		},
	},
	methods: {
		rowClicked({ guid }) {
			// dispatch action to show clicked connection
			this.$store.dispatch(getStoreAction('LOAD_AND_SHOW_CONNECTION', 'CB'), guid);
		},
		deleteClicked({ guid }) {
			// ask confirmation to delete connection
			this.$store.commit(getStoreMutation('OPEN_CONFIRMATION'), {
				title: this.$t('cb.connectionList.delete.confirm.title'),
				body: this.$t('cb.connectionList.delete.confirm.body'),
				confirmButtonText: this.$t(
					'cb.connectionList.delete.confirm.confirmButtonText'
				),
				confirmFn: () => {
					// dispatch action to delete clicked connection
					this.$store.dispatch(
						getStoreAction('DELETE_CONNECTION', 'CB'),
						{
							guid,
						}
					);
				},
			});
		},
		sortByDate(a, b, isAsc) {
			return Helpers.date.sortByIsoDate(a.time, b.time, isAsc);
		},
	},
	unmounted: function () {
		// unsubscribe from actions
		this.unsubscribeAction();
	},
};
</script>
